"use client";

import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Typography, Button, IconButton, Chip, Collapse, Tooltip } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import { colors, typography } from '@/theme/colors';
import RevokeKeyDialog from './RevokeKeyDialog';
interface ApiKeyCardProps {
  keyData: {
    id: number;
    name: string;
    prefix: string;
    is_active: boolean;
    created_at: string;
    last_used_at: string | null;
  };
  onRevoke: (keyId: number) => void;
  index: number;
}
const ApiKeyCard: React.FC<ApiKeyCardProps> = ({
  keyData,
  onRevoke,
  index
}) => {
  const t = useTranslations('apiKeys');
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(keyData.prefix);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  const handleRevokeConfirm = async () => {
    setIsRevoking(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/api-keys/${keyData.id}`, {
        method: 'DELETE',
        headers: {
          ...(token && {
            Authorization: `Bearer ${token}`
          })
        }
      });
      if (!res.ok) throw new Error('Failed to revoke key');
      onRevoke(keyData.id);
      setRevokeDialogOpen(false);
    } catch (err) {
      console.error('Failed to revoke:', err);
    } finally {
      setIsRevoking(false);
    }
  };
  const getLastUsedText = (): string => {
    if (!keyData.last_used_at) return t('neverUsed');
    return formatDistanceToNow(new Date(keyData.last_used_at), {
      addSuffix: true
    });
  };
  const getCreatedText = (): string => {
    return formatDistanceToNow(new Date(keyData.created_at), {
      addSuffix: true
    });
  };
  const isRevoked = !keyData.is_active;
  return <>
      <Box sx={{
      bgcolor: colors.background.paper,
      border: `1px solid ${colors.border.light}`,
      borderRadius: '12px',
      p: 3,
      opacity: isRevoked ? 0.6 : 1,
      animation: 'fadeInUp 0.3s ease-out forwards',
      animationDelay: `${index * 50}ms`,
      '@keyframes fadeInUp': {
        from: {
          opacity: 0,
          transform: 'translateY(8px)'
        },
        to: {
          opacity: isRevoked ? 0.6 : 1,
          transform: 'translateY(0)'
        }
      },
      transition: 'all 0.2s cubic-bezier(0.22, 1, 0.36, 1)',
      '&:hover': isRevoked ? {} : {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        borderColor: colors.border.medium
      }
    }} data-sentry-element="Box" data-sentry-source-file="ApiKeyCard.tsx">
        {/* Header Row */}
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 2
      }} data-sentry-element="Box" data-sentry-source-file="ApiKeyCard.tsx">
          <Box data-sentry-element="Box" data-sentry-source-file="ApiKeyCard.tsx">
            <Typography sx={{
            ...typography.presets.cardTitle,
            color: colors.text.primary,
            textDecoration: isRevoked ? 'line-through' : 'none'
          }} data-sentry-element="Typography" data-sentry-source-file="ApiKeyCard.tsx">
              {keyData.name || 'API Key'}
            </Typography>
            <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 0.5
          }} data-sentry-element="Box" data-sentry-source-file="ApiKeyCard.tsx">
              <Typography sx={{
              ...typography.presets.caption,
              color: colors.text.tertiary
            }} data-sentry-element="Typography" data-sentry-source-file="ApiKeyCard.tsx">
                {t('keyId')}:
              </Typography>
              <Typography sx={{
              fontFamily: 'monospace',
              fontSize: '12px',
              color: colors.text.tertiary
            }} data-sentry-element="Typography" data-sentry-source-file="ApiKeyCard.tsx">
                {keyData.prefix}
              </Typography>
              <Tooltip title={copied ? t('copySuccess') : t('copyKeyId')} data-sentry-element="Tooltip" data-sentry-source-file="ApiKeyCard.tsx">
                <IconButton size="small" onClick={handleCopy} aria-label={t('copyKeyId')} sx={{
                minWidth: '44px',
                minHeight: '44px',
                padding: '10px',
                transition: 'all 0.15s ease',
                '&:active': {
                  transform: 'scale(0.95)'
                }
              }} data-sentry-element="IconButton" data-sentry-source-file="ApiKeyCard.tsx">
                  {copied ? <CheckIcon sx={{
                  fontSize: 16,
                  color: colors.status.success.main
                }} /> : <ContentCopyIcon sx={{
                  fontSize: 16
                }} />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-sentry-element="Box" data-sentry-source-file="ApiKeyCard.tsx">
            <Chip label={isRevoked ? t('revoked') : t('active')} size="small" sx={{
            bgcolor: isRevoked ? colors.status.error.light : colors.status.success.light,
            color: isRevoked ? colors.status.error.main : colors.status.success.main,
            fontWeight: 500,
            fontSize: '12px'
          }} data-sentry-element="Chip" data-sentry-source-file="ApiKeyCard.tsx" />
            {!isRevoked && <Typography sx={{
            ...typography.presets.caption,
            color: colors.text.tertiary
          }}>
                • {getLastUsedText()}
              </Typography>}
          </Box>
        </Box>

        {/* Expand/Collapse Section */}
        {keyData.is_active && <>
            <Button size="small" onClick={() => setExpanded(!expanded)} endIcon={expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />} sx={{
          textTransform: 'none',
          color: colors.text.tertiary,
          minWidth: '44px',
          minHeight: '44px',
          '&:hover': {
            bgcolor: 'transparent'
          }
        }}>
              {expanded ? t('hideDetails') : t('viewDetails')}
            </Button>

            <Collapse in={expanded}>
              <Box sx={{
            bgcolor: colors.background.light,
            p: 2,
            borderRadius: '8px',
            mt: 1
          }}>
                <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}>
                  <Typography sx={{
                ...typography.presets.caption,
                color: colors.text.tertiary
              }}>
                    <strong>{t('created')}:</strong> {getCreatedText()}
                  </Typography>
                  <Typography sx={{
                ...typography.presets.caption,
                color: colors.text.tertiary
              }}>
                    <strong>{t('lastUsed')}:</strong> {getLastUsedText()}
                  </Typography>
                </Box>
              </Box>
            </Collapse>
          </>}

        {/* Actions */}
        {keyData.is_active && <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        mt: 2
      }}>
            <Button size="small" onClick={() => setRevokeDialogOpen(true)} sx={{
          textTransform: 'none',
          color: colors.status.error.main,
          minWidth: '44px',
          minHeight: '44px',
          transition: 'all 0.15s ease',
          '&:hover': {
            bgcolor: colors.status.error.light
          },
          '&:active': {
            transform: 'scale(0.97)'
          }
        }}>
              {t('revokeKey')}
            </Button>
          </Box>}
      </Box>

      <RevokeKeyDialog open={revokeDialogOpen} keyName={keyData.name || 'API Key'} keyPrefix={keyData.prefix} onCancel={() => setRevokeDialogOpen(false)} onConfirm={handleRevokeConfirm} isLoading={isRevoking} data-sentry-element="RevokeKeyDialog" data-sentry-source-file="ApiKeyCard.tsx" />
    </>;
};
export default ApiKeyCard;